import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Radio } from "lucide-react";

function fmtDuration(s) {
  const m = Math.floor(s / 60); const sec = Math.floor(s % 60);
  if (m >= 60) { const h = Math.floor(m / 60); return `${h}h ${m % 60}m`; }
  if (m > 0) return `${m}m`;
  return `${sec}s`;
}

/**
 * Global Active Session indicator. Shown whenever a session is running.
 * Reads /api/sessions/active every ~15s (and once on mount). Clicking navigates to that song's Sessions tab.
 */
export function ActiveSessionBanner({ triggerReload = 0 }) {
  const [session, setSession] = useState(null);
  const [song, setSong] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/sessions/active");
      setSession(data || null);
      if (data?.song_id) {
        try { const { data: s } = await api.get(`/songs/${data.song_id}`); setSong(s); } catch {}
      } else setSong(null);
    } catch { setSession(null); setSong(null); }
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  }, [triggerReload]);

  useEffect(() => {
    if (!session) { setElapsed(0); return; }
    const start = new Date(session.started_at).getTime();
    const tick = () => setElapsed(Math.floor((Date.now() - start) / 1000));
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [session]);

  if (!session) return null;

  const label = song ? song.name : "Session";
  const go = () => { if (session.song_id) navigate(`/songs/${session.song_id}`); };

  return (
    <button
      onClick={go}
      data-testid="active-session-banner"
      className="hq-session-pos fixed z-40 left-1/2 -translate-x-1/2 md:left-auto md:right-6 md:translate-x-0 flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-200 text-xs shadow-lg backdrop-blur-md hover:bg-emerald-500/20 transition-colors"
    >
      <span className="relative flex h-2 w-2">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400/70"></span>
        <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400"></span>
      </span>
      <Radio className="w-3.5 h-3.5" />
      <span className="font-medium truncate max-w-[140px] md:max-w-[220px]">{label}</span>
      <span className="mono tabular text-emerald-100/90">{fmtDuration(elapsed)}</span>
    </button>
  );
}
